/**
 * Výber variant pre výstup (KOLO 206).
 *
 * User: *„keď posielam na pílu alebo robím objednanie, môžem vybrať, ktorú
 * variantu chcem, aby sa exportovala — výpočet, zákazku aj AA"*. Do KOLA
 * 206 išla do výstupu len otvorená záložka a v okne nebolo napísané ktorá.
 *
 * Okno sa ukáže len pri zákazke s viac variantami; predvolená je otvorená
 * záložka, takže Enter dá to isté ako doteraz. Viac zaškrtnutých = jeden
 * dokument, kódy s číslom záložky (`1-A1`, `2-A1`).
 */
import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { formatEur } from "@/lib/format";
import { druhVarianty, nazovVarianty } from "@/lib/variantyZakazky";
import type { Varianta } from "@/api/schemas";

export function VyberVariantDialog({
  open,
  onOpenChange,
  titul,
  potvrdit,
  poznamka,
  varianty,
  otvorenaId,
  onPotvrd,
  bezi = false,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Názov výstupu — „Súpis pre objednávky". */
  titul: string;
  /** Text tlačidla — „Zobraziť PDF", „Stiahnuť XLSX". */
  potvrdit: string;
  /** Čo znamená viac variant pre tento výstup (tabule spolu…). */
  poznamka?: string;
  /** Varianty v poradí záložiek (`poradieVariant`). */
  varianty: Varianta[];
  otvorenaId: string;
  /** Vybrané id v poradí záložiek. */
  onPotvrd: (ids: string[]) => void;
  bezi?: boolean;
}): JSX.Element {
  const [vybrane, setVybrane] = useState<Set<string>>(new Set());
  const casti = varianty.filter((v) => v.je_cast);

  useEffect(() => {
    if (open) setVybrane(new Set([otvorenaId]));
  }, [open, otvorenaId]);

  function prepni(id: string): void {
    setVybrane((pred) => {
      const nove = new Set(pred);
      if (nove.has(id)) nove.delete(id);
      else nove.add(id);
      return nove;
    });
  }

  const ids = varianty.filter((v) => vybrane.has(v.id)).map((v) => v.id);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{titul}</DialogTitle>
          <DialogDescription>
            Z ktorých variant zákazky — viac zaškrtnutých je jeden dokument.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => setVybrane(new Set([otvorenaId]))}
          >
            Len otvorená
          </Button>
          {casti.length > 1 ? (
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => setVybrane(new Set(casti.map((v) => v.id)))}
            >
              Všetky časti
            </Button>
          ) : null}
        </div>
        <ul className="max-h-72 space-y-1 overflow-y-auto">
          {varianty.map((v, i) => (
            <li key={v.id}>
              <label className="flex cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-sm hover:bg-accent">
                <input
                  type="checkbox"
                  className="h-4 w-4 accent-primary"
                  checked={vybrane.has(v.id)}
                  onChange={() => prepni(v.id)}
                  aria-label={nazovVarianty(v)}
                />
                <span className="w-5 shrink-0 font-mono text-xs text-muted-foreground">
                  {i + 1}
                </span>
                <span className="min-w-0 flex-1 truncate">
                  {nazovVarianty(v)}
                  {v.id === otvorenaId ? (
                    <span className="text-muted-foreground"> · otvorená</span>
                  ) : null}
                </span>
                <span className="shrink-0 text-xs text-muted-foreground">
                  {druhVarianty(v)}
                  {!v.je_vyrobna && v.cena_s_dph != null
                    ? ` · ${formatEur(v.cena_s_dph)}`
                    : ""}
                </span>
              </label>
            </li>
          ))}
        </ul>
        <p className="text-[11px] text-muted-foreground">
          Pri viacerých majú zostavy a dielce číslo varianty: 1-A1, 2-A1.
          {poznamka ? ` ${poznamka}` : ""}
        </p>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={bezi}
          >
            Zrušiť
          </Button>
          <Button
            type="button"
            disabled={bezi || ids.length === 0}
            onClick={() => onPotvrd(ids)}
          >
            {bezi
              ? "Generuje sa…"
              : ids.length > 1
                ? `${potvrdit} (${ids.length} ${ids.length < 5 ? "varianty" : "variant"})`
                : potvrdit}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
